import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useEffect, useRef, useState } from 'react';
import { useSite } from '../../context/site';
import ServiceDateTemplate from '../service-date-template';
import ServiceGroupUpdate from '../service/group/update';
import DateRangeReport from './date-range-select';

function StateReport5() {
    const { GetStateReport5 } = useSite();

    const dt = useRef(null);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dateBegin, setDateBegin] = useState(new Date());
    const [dateEnd, setDateEnd] = useState(new Date());
    const [run, setRun] = useState(true);
    const [familiesCount, setFamiliesCount] = useState(0);
    const [familiesUniqueCount, setFamiliesUniqueCount] = useState(0);
    const [childrenCount, setChildrenCount] = useState(0);
    const [childrenUniqueCount, setChildrenUniqueCount] = useState(0);
    const [unknownFamiliesCount, setUnknownFamiliesCount] = useState(0);
    const [unknownChildrenCount, setUnknownChidlrenCount] = useState(0);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});

    const exportCSV = (selectionOnly) => {
        dt.current.exportCSV({ selectionOnly });
    };

    const ActionsDisplay = (service) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateData(service);
                        setOpenUpdate(true);
                    }}
                />
            </>
        );
    };

    const NotesDisplay = (service) => {
        if (!service?.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: service.notes }}></span>;
    };

    const EmployeesDisplay = (service) => {
        if (!Array.isArray(service?.employees)) {
            return '';
        }

        return <span>{service.employees.map((e) => e.name).join(', ')}</span>;
    };

    const ChildrenCountDisplay = (service) => {
        if (!Array.isArray(service?.parentsChildren)) {
            return '';
        }

        const countChildren = service.parentsChildren.reduce((p, c) => p + c.children.length, 0);

        return <span>{countChildren}</span>;
    };

    // On rows change
    useEffect(() => {
        const countFamilies = rows.reduce((prev, curr) => {
            return prev + curr.numberOfParents;
        }, 0);

        const countChildren = rows.reduce((prev, curr) => {
            return prev + curr.parentsChildren.reduce((p, c) => p + c.children.length, 0);
        }, 0);

        const countUnknownFamilies = rows.reduce((prev, curr) => {
            return prev + curr.unknownParents;
        }, 0);

        const countUnknownChildren = rows.reduce((prev, curr) => {
            return prev + curr.unknownChildren;
        }, 0);

        const setFamiliesIdsUnique = new Set();
        const setChildrenIdsUnique = new Set();

        rows.forEach((row) => {
            row.parentsChildren.forEach((pc) => {
                setFamiliesIdsUnique.add(pc.parent._id);

                pc.children.forEach((c) => {
                    setChildrenIdsUnique.add(c._id);
                });
            });
        });

        setFamiliesCount(countFamilies);
        setFamiliesUniqueCount(setFamiliesIdsUnique.size);
        setChildrenCount(countChildren);
        setChildrenUniqueCount(setChildrenIdsUnique.size);
        setUnknownFamiliesCount(countUnknownFamilies);
        setUnknownChidlrenCount(countUnknownChildren);
    }, [rows]);

    if (run) {
        return (
            <>
                <DateRangeReport
                    onRunReport={({ start, end }) => {
                        setLoading(true);
                        setDateBegin(new Date(start));
                        setDateEnd(new Date(end));
                        GetStateReport5({ start, end }, (res) => {
                            setRows(res.data.rows);
                            setLoading(false);
                            setRun(false);
                        });
                    }}
                />
            </>
        );
    }

    return (
        <>
            <div className="mt-2">
                <Button label="Change Date" icon="pi pi-check" onClick={() => setRun(true)} />
            </div>

            <div className="mt-2">
                <span>Start Date: {dateBegin.toLocaleDateString()}</span>
            </div>

            <div className="mt-2">
                <span>End Date: {dateEnd.toLocaleDateString()}</span>
            </div>

            <div className="mt-2">
                <span>Number of Services&nbsp;</span>
                <Badge value={rows.length} severity="success"></Badge>
            </div>

            <div className="mt-2">
                <span>Families&nbsp;</span>
                <Badge value={familiesCount} severity="info"></Badge>
                <span>&nbsp;-&nbsp;Unique&nbsp;</span>
                <Badge value={familiesUniqueCount} severity="info"></Badge>
                <span>&nbsp;-&nbsp;Unknown&nbsp;</span>
                <Badge value={unknownFamiliesCount} severity="info"></Badge>
                <span>&nbsp;-&nbsp;Total (unique)&nbsp;</span>
                <Badge value={unknownFamiliesCount + familiesUniqueCount} severity="info"></Badge>
                <span>&nbsp;-&nbsp;Total (non unique)&nbsp;</span>
                <Badge value={unknownFamiliesCount + familiesCount} severity="info"></Badge>
            </div>

            <div className="mt-2">
                <span>Children&nbsp;</span>
                <Badge value={childrenCount} severity="warning"></Badge>
                <span>&nbsp;-&nbsp;Unique&nbsp;</span>
                <Badge value={childrenUniqueCount} severity="warning"></Badge>
                <span>&nbsp;-&nbsp;Unknown&nbsp;</span>
                <Badge value={unknownChildrenCount} severity="warning"></Badge>
                <span>&nbsp;-&nbsp;Total (unique)&nbsp;</span>
                <Badge
                    value={unknownChildrenCount + childrenUniqueCount}
                    severity="warning"
                ></Badge>
                <span>&nbsp;-&nbsp;Total (non unique)&nbsp;</span>
                <Badge value={unknownChildrenCount + childrenCount} severity="warning"></Badge>
            </div>

            <div className="mt-2">
                <DataTable
                    ref={dt}
                    header={
                        <Button
                            type="button"
                            icon="pi pi-file"
                            rounded
                            onClick={() => exportCSV(false)}
                            data-pr-tooltip="CSV"
                        />
                    }
                    value={rows}
                    emptyMessage="No group services found"
                    showGridlines
                    removableSort
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    loading={loading}
                >
                    <Column
                        field="dateService"
                        body={ServiceDateTemplate}
                        header="Service Date"
                    ></Column>
                    <Column field="serviceType.itemName" header="Service Type"></Column>
                    <Column body={EmployeesDisplay} header="Employees"></Column>
                    <Column field="numberOfParents" header="Families"></Column>
                    <Column body={ChildrenCountDisplay} header="Children"></Column>
                    <Column field="unknownParents" header="Unknown Families"></Column>
                    <Column field="unknownChildren" header="Unknown Children"></Column>
                    <Column body={NotesDisplay} header="Notes"></Column>
                    <Column
                        header="Actions"
                        bodyStyle={{ width: '150px' }}
                        body={ActionsDisplay}
                    ></Column>
                </DataTable>

                {!openUpdate ? null : (
                    <ServiceGroupUpdate
                        open={openUpdate}
                        data={updateData}
                        onClose={() => {
                            setUpdateData({});
                            setOpenUpdate(false);
                        }}
                    />
                )}
            </div>
        </>
    );
}

export default StateReport5;
